import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Clock, ChevronRight, CheckCircle, 
  PlayCircle, BarChart2, Search, X, Award, 
  Target, TrendingUp, Calendar, User, Layers,
  Filter, ArrowUpRight, Sparkles, Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Link } from "react-router-dom";
import Footer from '../../components/Footer';
import { postRequest } from '../../utils/networking';
import { apiUrls, imageUrl } from '../../utils/url';

// Helper functions for image processing
function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = String(fullPath).replace(/\\/g, "/");
  const assetsIndex = normalized.indexOf("assets/");
  if (assetsIndex !== -1) {
    return normalized.substring(assetsIndex + 7);
  }
  const asstesIndex = normalized.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return normalized.substring(asstesIndex + 7);
  }
  return normalized;
}

const getProcessedImageUrl = (imageUrlSource) => {
  const FALLBACK_IMAGE =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" font-size="14" text-anchor="middle" dy=".3em" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';

  if (!imageUrlSource) return FALLBACK_IMAGE;

  if (String(imageUrlSource).startsWith('http')) {
    return imageUrlSource;
  }

  const extractedPath = getPath(imageUrlSource);
  if (!extractedPath) return FALLBACK_IMAGE;

  const cleanPath = extractedPath.replace(/^\/+/, '');
  return `${imageUrl}/${cleanPath}`;
};

const MyCoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const response = await postRequest(apiUrls.getmycourseorders);
        console.log("My courses response:", response);
        
        if (response.status === "S" || response.status === "s") {
          const coursesData = response.data || [];
          
          const processedCourses = coursesData.map(course => ({
            id: course.courseid,
            courseid: course.courseid,
            title: course.coursename || "Untitled Course",
            contact: course.contact || "",
            thumbnail: getProcessedImageUrl(course.imgurl || course.thumbnail),
            amount: course.amount || 0,
            accesstype: course.accesstype || "PURCHASED",
            totalLessons: course.curriculum ? course.curriculum.length : 0,
            category: "General",
            level: "Beginner"
          }));
          setCourses(processedCourses);
        } else {
          console.error("Failed to fetch my courses:", response.message);
          setCourses([]);
        }
      } catch (error) {
        console.error("Error in fetchCourses:", error);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const filteredCourses = courses.filter(course => {
    if (filter !== 'all' && course.category !== filter) return false;

    if (
      debouncedSearchTerm &&
      !String(course.title || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase()) &&
      !String(course.contact || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

  const getStatusBadge = (accesstype) => {
    if (accesstype === 'PURCHASED') return { label: 'Purchased', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' };
    return { label: accesstype, color: 'bg-slate-50 text-slate-600 border-slate-100' };
  };

  const calculateStats = () => {
    const totalCourses = courses.length;
    const totalInvestment = courses.reduce((acc, c) => acc + (c.amount || 0), 0);
    const totalLessons = courses.reduce((acc, c) => acc + (c.totalLessons || 0), 0);
    
    return { totalCourses, totalInvestment, totalLessons };
  };

  const stats = calculateStats();

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const categories = [
    { id: 'all', label: 'All Courses', icon: <Layers size={16} /> },
    { id: 'frontend', label: 'Frontend', icon: <Sparkles size={16} /> },
    { id: 'backend', label: 'Backend', icon: <Zap size={16} /> },
    { id: 'data-science', label: 'Data Science', icon: <BarChart2 size={16} /> }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 pt-24 px-4 sm:px-6 lg:px-8">
        <div className="min-w-screen ">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-white rounded-lg border border-slate-100 "></div>
            <div className="flex gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-32 bg-white rounded-full border border-slate-100"></div>)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="bg-white rounded-lg h-96 border border-slate-100 "></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col pt-24">
      <div className="flex-grow pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero Section - Orange & White Theme */}
        <div className="relative overflow-hidden rounded-lg bg-white mb-6 px-6 py-2 md:px-8 md:py-3 border border-orange-50">
          {/* Background Decorative Elements */}
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-orange-500/10 blur-[100px] rounded-full"></div>
          <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-96 h-96 bg-amber-500/5 blur-[100px] rounded-full"></div>
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-600 text-[9px] font-semibold mb-2 capitialize">
                <TrendingUp size={12} />
                Learning Dashboard
              </div>
                <div className="flex flex-wrap gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100">
                      <Layers size={17} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-medium Capitialize">Total Modules</p>
                      <p className="text-xs font-bold text-slate-800">{stats.totalCourses}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-100 ">
                      <PlayCircle size={17} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-medium Capitialize">Total Videos</p>
                      <p className="text-xs font-bold text-slate-800">{stats.totalLessons}</p>
                    </div>
                  </div>
                </div>
            </div>

            <div className="lg:w-1/3">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500  rounded-lg p-2 px-3 text-white">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-sm">Learning Journey</h3>
                  <Award className="text-white/80" size={16} />
                </div>
                <div className="flex justify-between items-end mt-4">
                  <div className="leading-none">
                    <p className="text-xl font-black">{stats.totalLessons}</p>
                    <p className="text-[9px] text-white/70">Videos Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Search Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <h2 className="text-2xl font-bold text-slate-800 ml-5">Courses</h2>
          <div className="relative group w-full md:w-80">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-orange-500 transition-colors">
              <Search size={18} />
            </div>
            <input
              type="text"
              placeholder="Search your courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-sm bg-white border border-slate-200 rounded-xl py-2 pl-12 pr-10 focus:outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all  text-slate-700 placeholder:text-slate-400"
            />
            {searchTerm && (
              <button 
                onClick={clearSearch}
                className="absolute inset-y-0 right-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Course Grid */}
        {filteredCourses.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-100  p-20 text-center">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search size={40} className="text-slate-300" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">No matching courses</h3>
            <p className="text-slate-500 max-w-sm mx-auto mb-8">
              We couldn't find any courses matching your search or filters. Try adjusting them to see more content.
            </p>
            <button 
              onClick={clearSearch}
              className="px-8 py-3 bg-orange-500 text-white rounded-2xl font-bold hover:bg-orange-600 transition-all "
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCourses.map((course, index) => {
              const status = getStatusBadge(course.accesstype);
              return (
                <div 
                  key={`${course.id}-${index}`}
                  className="group relative bg-white rounded-2xl border border-slate-100  transition-all duration-500 flex flex-col overflow-hidden"
                >
                  {/* Thumbnail Container */}
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={course.thumbnail} 
                      alt={course.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Floating Badges */}
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold border backdrop-blur-md  ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    
                  
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 scale-50 group-hover:scale-100">
                      <div className="w-16 h-16 rounded-full bg-orange-500/90 text-white flex items-center justify-center">
                        <PlayCircle size={32} fill="currentColor" fillOpacity={0.3} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10px] font-bold capitialize">
                        {course.level}
                      </span>
                      <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                      <span className="text-[10px] text-slate-400 font-medium capitialize">
                        {course.category}
                      </span>
                    </div>
                    
                    <h3 className="text-base font-bold text-slate-800 mb-1 leading-snug group-hover:text-orange-600 transition-colors line-clamp-2">
                      {course.title}
                    </h3>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 border border-slate-200">
                        <User size={10} />
                      </div>
                      <p className="text-sm text-slate-500 font-medium">{course.contact || "N/A"}</p>
                    </div>

                    <div className="mt-auto space-y-2">
                      {/* Course Details Section */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-bold text-slate-700">₹{course.amount}</span>
                          <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
                            <PlayCircle size={12} /> {course.totalLessons} Videos
                          </span>
                        </div>
                      </div>

                      <Link to="/coursedetail" state={{ id: course.courseid }} className="block">
                        <button className="w-full  bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2  active:scale-[0.98]">
                          View Course
                          <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
      <Footer />
    </div>
    
  );
};

export default MyCoursesPage;
